import { useCallback, useEffect, useState  } from 'react';
import { CompanySmall } from 'api';
import { COMPANIES_SLUG, DASHBOARD_SLUG, OPERATIONS_SLUG } from 'dashboard409a/common/constants/urls';
import { SimplifiedAttioValuation } from '../dashboard409a/services/attio.types';

/**
 * Hook to handle mapping between Attio valuations and standalone companies
 */

// Constants
const UNKNOWN_COMPANY_SLUG = 'unknown-company';
const MEASUREMENT_DATE_PARAM = 'md';

// Types
interface UseCompanyLookupResult {
  matchedCompany: CompanySmall | null;
  isLoading: boolean;
  error: string | null;
  generateOperationsUrl: (valuationId: string, measurementDate?: string) => string | null;
  formatMeasurementDate: (valuationDate: string | null) => string | undefined;
}

/**
 * Clean company name for better matching
 * Removes common suffixes and normalizes the name
 */
const cleanCompanyName = (name: string): string => {
  if (!name) {
    return '';
  }

  // Convert to lowercase and then clean
  let cleanedName = name.toLowerCase();

  // Find the index of the first common separator
  const separatorIndex = cleanedName.indexOf('--');
  const pipeIndex = cleanedName.indexOf('|');

  let splitIndex = -1;
  
  if (separatorIndex !== -1 && (pipeIndex === -1 || separatorIndex < pipeIndex)) {
    splitIndex = separatorIndex;
  } else if (pipeIndex !== -1) {
    splitIndex = pipeIndex;
  }

  // If a separator is found, truncate the string at that point
  if (splitIndex !== -1) {
    cleanedName = cleanedName.substring(0, splitIndex);
  }

  // Remove common legal suffixes and punctuation after splitting
  cleanedName = cleanedName
    .replaceAll(/\b(inc|llc|ltd|corp|co)\b/g, '')
    .replaceAll(/[.,()]/g, '')
    .replaceAll(/\s+/g, ' ')
    .trim();

  return cleanedName;
};

/**
 * Find a standalone company that matches the Attio valuation data
 * Currently matches by company name, but can be enhanced to use attio_company_id
 */
export const findStandaloneCompanyForValuation = (
  valuation: SimplifiedAttioValuation,
  standaloneCompanies: CompanySmall[]
): CompanySmall | null => {
  if (!valuation.company_name || !standaloneCompanies?.length) {
    return null;
  }

  const cleanedValuedEntityName = cleanCompanyName(valuation.company_name);

  // First, try to find by exact name match (after cleaning)
  const exactMatch = standaloneCompanies.find(company => {
    if (!company.name) {
      return false;
    }
    const cleanedCompanyName = cleanCompanyName(company.name);
    return cleanedCompanyName === cleanedValuedEntityName;
  });
  if (exactMatch) {
    return exactMatch;
  }

  // If no exact match, try to find by partial name match
  // This handles cases where company names might have slight variations
  const partialMatch = standaloneCompanies.find(company => {
    if (!company.name) {
      return false;
    }
    
    const cleanedCompanyName = cleanCompanyName(company.name);
    
    // Check if either name contains the other (accounting for variations)
    return cleanedCompanyName.includes(cleanedValuedEntityName) || 
           cleanedValuedEntityName.includes(cleanedCompanyName);
  });
  return partialMatch || null;
};

/**
 * Extract measurement date from valuation data
 * Formats it in the expected format for the URL
 */
export const formatMeasurementDate = (valuationDate: string | null): string | undefined => {
  if (!valuationDate) {
    return undefined;
  }
  
  try {
    // Just return the date in YYYY-MM-DD format without fake IDs
    const date = new Date(valuationDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch {
    return undefined;
  }
};

/**
 * Generate the proper dashboard URL for a standalone company and valuation
 * Standalone version for direct use without the hook
 */
export const generateCompanyOperationsUrl = (
  company: CompanySmall,
  valuationId: string,
  measurementDate?: string
): string => {
  const companySlug = company.slug || UNKNOWN_COMPANY_SLUG;
  
  // Simple URL structure: /dashboard/companies/{name}-{id}/operations/{valuationId}
  let url = `/${DASHBOARD_SLUG}/${COMPANIES_SLUG}/${companySlug}/${OPERATIONS_SLUG}/${valuationId}`;
  
  // Add measurement date as query parameter if provided
  if (measurementDate) {
    url += `?${MEASUREMENT_DATE_PARAM}=${measurementDate}`;
  }
  
  return url;
};

/**
 * Main hook for company lookup functionality
 */
export const useCompanyLookup = (
  valuation: SimplifiedAttioValuation | null,
  standaloneCompanies: CompanySmall[] | null
): UseCompanyLookupResult => {
  const [matchedCompany, setMatchedCompany] = useState<CompanySmall | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find matching company when valuation or companies change
  useEffect(() => {
    if (!valuation || !standaloneCompanies) {
      setMatchedCompany(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const company = findStandaloneCompanyForValuation(valuation, standaloneCompanies);
      setMatchedCompany(company);
      
      if (!company) {
        setError(`No matching company found for "${valuation.company_name}"`);
      }
    } catch (err) {
      setError('Error finding matching company');
      // eslint-disable-next-line no-console
      console.error('Company lookup error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [valuation, standaloneCompanies]);

  // Generate operations URL callback using the exported function
  const generateOperationsUrl = useCallback((valuationId: string, measurementDate?: string): string | null => {
    if (!matchedCompany) {
      return null;
    }

    return generateCompanyOperationsUrl(matchedCompany, valuationId, measurementDate);
  }, [matchedCompany]);

  return {
    matchedCompany,
    isLoading,
    error,
    generateOperationsUrl,
    formatMeasurementDate,
  };
};